import React, { useCallback } from 'react';
import { Form, Input, Checkbox } from 'antd';
import { AddItemFormData, PricingDetail, PricingOption } from './types';

interface PricingStepProps {
  styles: { [key: string]: string };
  formData: AddItemFormData;
  setFormData: React.Dispatch<React.SetStateAction<AddItemFormData>>;
  pricingOptions: PricingOption[];
  isWearable: boolean;
  isSkill?: boolean;
  isWorkflow?: boolean;
}

const PricingStep: React.FC<PricingStepProps> = ({ styles, formData, setFormData, pricingOptions, isWearable, isSkill, isWorkflow }) => {
  const handlePricingToggle = useCallback((values: string[]) => {
    const freeOptions = ['free', 'freemium'];
    const paidOptions = ['renting', 'purchasing', 'payPerUse'];
    
    // Check if both free and paid options are selected
    const selectedFree = values.some(v => freeOptions.includes(v));
    const selectedPaid = values.some(v => paidOptions.includes(v));
    
    // If both types are selected, implement mutual exclusivity
    let finalValues = values;
    if (selectedFree && selectedPaid) {
      // Find which option was just added/removed by comparing with current state
      const previousFree = formData.pricingModel.some(v => freeOptions.includes(v));
      const previousPaid = formData.pricingModel.some(v => paidOptions.includes(v));
      
      // If user just selected a free option, remove all paid options
      if (!previousFree && selectedFree) {
        finalValues = values.filter(v => freeOptions.includes(v));
      } 
      // If user just selected a paid option, remove all free options
      else if (!previousPaid && selectedPaid) {
        finalValues = values.filter(v => paidOptions.includes(v));
      }
    }
    
    const newDetails = { ...formData.pricingDetails };
    finalValues.forEach(v => {
      if (!newDetails[v]) {
        newDetails[v] = { price: 0, description: '' };
      }
    });
    Object.keys(newDetails).forEach(k => {
      if (!finalValues.includes(k)) {
        delete newDetails[k];
      }
    });
    setFormData(prev => ({ ...prev, pricingModel: finalValues, pricingDetails: newDetails }));
  }, [formData.pricingDetails, formData.pricingModel, setFormData]);

  const updatePricingDetail = useCallback((model: string, field: keyof PricingDetail, value: any) => {
    setFormData(prev => ({
      ...prev,
      pricingDetails: {
        ...prev.pricingDetails,
        [model]: {
          ...prev.pricingDetails[model],
          [field]: value
        }
      }
    }));
  }, [setFormData]);
  // const hardCodedPricingOptions: PricingOption[] = [
  //   {
  //       "value": "purchasing",
  //       "id": "69b3b1a3eb1e7496ca8acd58",
  //       "label": "One-time Purchase",
  //       "desc": "Charge a single fee for lifetime access to your agent",
  //       "allowMultiple": true
  //   }
  // ]
console.log('Pricing opt', pricingOptions)
  return (
    <div className={styles.stepContainer}>
      <h2 className={styles.stepTitle}>Pricing and Interfaces</h2>
      
      <div className={styles.pricingOptions}>
        <Form.Item
          rules={[{ required: true, message: 'Please select a pricing model' }]}
        >
          <Checkbox.Group 
            className={styles.radioGroup}
            value={formData.pricingModel}
            onChange={(values) => handlePricingToggle(values as string[])}
          >
          
          {pricingOptions.map(opt => ( 
          // {hardCodedPricingOptions.map(opt => (
            <div key={opt.value} className={styles.pricingOptionWrapper}>
              <div className={`${styles.pricingOption} ${formData.pricingModel.includes(opt.value) ? styles.pricingOptionSelected : ''} ${formData.pricingModel.includes(opt.value) && opt.value !== 'free' && opt.value !== 'freemium' ? styles.pricingOptionWithSub : ''}`}>
                <Checkbox value={opt.value} className={styles.radioOption}>
                  <div className={styles.optionContent}>
                    <strong>{opt.label}</strong>
                    <p>{opt.desc}</p>
                  </div>
                </Checkbox>
              </div>
              {formData.pricingModel.includes(opt.value) && opt.value !== 'free' && opt.value !== 'freemium' && (
                <div className={styles.pricingSubFields}>
                  <div className={styles.pricingSubField}>
                    <label>Price <span style={{ color: 'red' }}>*</span></label>
                    <Input
                      type="number"
                      onWheel={(e: any) => e.target.blur()}
                      min={0}
                      placeholder="Enter price in USD"
                      className={styles.textInput}
                      status={!formData.pricingDetails[opt.value]?.price ? 'error' : undefined}
                      value={formData.pricingDetails[opt.value]?.price || ''}
                      onChange={(e) => updatePricingDetail(opt.value, 'price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className={styles.pricingSubField}>
                    <label>Description</label>
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter pricing description"
                      className={styles.textInput}
                      value={formData.pricingDetails[opt.value]?.description || ''}
                      onChange={(e) => updatePricingDetail(opt.value, 'description', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
          </Checkbox.Group>
        </Form.Item>
      </div>

      {!isWearable && !isSkill && !isWorkflow && formData.itemType !== 'avatar' && (
        <Form.Item label="Supported Interfaces" className={styles.formItem}>
          <div className={styles.interfaceOptions}>
            <Checkbox.Group 
              value={formData.supportedInterfaces}
              onChange={(values) => setFormData(prev => ({...prev, supportedInterfaces: values as string[]}))}
            >
              <Checkbox value="chat" className={styles.interfaceCheckbox}>Chat</Checkbox>
              <Checkbox value="voice" className={styles.interfaceCheckbox}>Voice</Checkbox>
              <Checkbox value="api" className={styles.interfaceCheckbox}>API</Checkbox>
            </Checkbox.Group>
          </div>
        </Form.Item>
      )}
    </div>
  );
};

export default PricingStep;
